import { useMemo } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { sendingDomainContract } from "@common/contracts/sending-domain.contract";
import type {
    CreateSendingEmailRequest,
    SendingEmailResponse,
    UpdateSendingEmailRequest,
} from "@common/schemas/sending-email.schema";
import { SendingDomainStatus } from "@common/types/domains/emailSending";
import { useScope } from "@state/scopeContext.lib";
import { useCanManageWorkspace } from "@hooks/permissions/useCanManageWorkspace";
import { getBackendAPIClient } from "@lib/backendAPIClient";
import { useSendingDomains } from "./sendingDomain.api";

const sendingEmailClient = getBackendAPIClient(sendingDomainContract);

export const sendingEmailQueryKeys = {
    all: () => ["sending-emails"] as const,
    workspace: (workspaceId: string) =>
        [...sendingEmailQueryKeys.all(), workspaceId] as const,
};

function requireWorkspaceId(workspaceId: string | null | undefined): string {
    if (!workspaceId) {
        throw new Error("No active workspace");
    }
    return workspaceId;
}

export type SendingEmailWithDomain = SendingEmailResponse & {
    domain: string;
    // Verification lives on the parent sending domain, not the email row: an
    // address is sendable only once its domain reaches `verified`. Resolved
    // here so callers (e.g. the invoice From picker) can gate on a single flag.
    isVerified: boolean;
};

export function useSendingEmails() {
    const { workspaceId } = useScope();
    const canManageWorkspace = useCanManageWorkspace();
    const isEnabled = Boolean(workspaceId) && canManageWorkspace;
    const { data: domains, isLoading: isLoadingDomains } = useSendingDomains({
        enabled: isEnabled,
    });

    const { data: emails, isLoading: isLoadingEmails } = useQuery({
        queryKey: workspaceId
            ? sendingEmailQueryKeys.workspace(workspaceId)
            : sendingEmailQueryKeys.all(),
        queryFn: async (): Promise<SendingEmailResponse[]> => {
            const activeWorkspaceId = requireWorkspaceId(workspaceId);
            const result = await sendingEmailClient.listAllEmails({
                params: { workspaceId: activeWorkspaceId },
            });
            if (result.status !== 200) {
                throw new Error(
                    `Failed to load sending emails (status ${result.status})`,
                );
            }
            return result.body.emails;
        },
        enabled: isEnabled,
    });

    const isLoading = isLoadingDomains || isLoadingEmails;

    const data = useMemo<SendingEmailWithDomain[] | undefined>(() => {
        if (isLoading || !emails) {
            return undefined;
        }
        const domainById = new Map(
            (domains ?? []).map((domain) => [domain.id, domain]),
        );
        return emails.map((email) => {
            const domain = domainById.get(email.sendingDomainId);
            return {
                ...email,
                domain: domain?.domain ?? "",
                isVerified: domain?.status === SendingDomainStatus.verified,
            };
        });
    }, [emails, domains, isLoading]);

    return { data, isLoading };
}

export function useCreateSendingEmail() {
    const { workspaceId } = useScope();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({
            domainId,
            body,
        }: {
            domainId: string;
            body: CreateSendingEmailRequest;
        }): Promise<SendingEmailResponse> => {
            const activeWorkspaceId = requireWorkspaceId(workspaceId);
            const result = await sendingEmailClient.createEmail({
                params: { workspaceId: activeWorkspaceId, domainId },
                body,
            });
            if (result.status !== 201) {
                throw new Error(
                    `Failed to create sending email (status ${result.status})`,
                );
            }
            return result.body;
        },
        onSuccess: () => {
            if (workspaceId) {
                queryClient.invalidateQueries({
                    queryKey: sendingEmailQueryKeys.workspace(workspaceId),
                });
            }
        },
    });
}

export function useUpdateSendingEmail() {
    const { workspaceId } = useScope();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({
            emailId,
            body,
        }: {
            emailId: string;
            body: UpdateSendingEmailRequest;
        }): Promise<SendingEmailResponse> => {
            const activeWorkspaceId = requireWorkspaceId(workspaceId);
            const result = await sendingEmailClient.updateEmail({
                params: { workspaceId: activeWorkspaceId, emailId },
                body,
            });
            if (result.status !== 200) {
                throw new Error(
                    `Failed to update sending email (status ${result.status})`,
                );
            }
            return result.body;
        },
        onSuccess: () => {
            if (workspaceId) {
                queryClient.invalidateQueries({
                    queryKey: sendingEmailQueryKeys.workspace(workspaceId),
                });
            }
        },
    });
}

export function useDeleteSendingEmail() {
    const { workspaceId } = useScope();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (emailId: string) => {
            const activeWorkspaceId = requireWorkspaceId(workspaceId);
            await sendingEmailClient.deleteEmail({
                params: { workspaceId: activeWorkspaceId, emailId },
            });
        },
        onSuccess: () => {
            if (workspaceId) {
                queryClient.invalidateQueries({
                    queryKey: sendingEmailQueryKeys.workspace(workspaceId),
                });
            }
        },
    });
}
